
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import Header from '@/components/Header.jsx';
import Footer from '@/components/Footer.jsx';
import { motion } from 'framer-motion';
import Seo from '@/components/Seo.jsx';

function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Seo
        title="Politique de Confidentialité | Neuvième Continent"
        description="Politique de confidentialité et protection des données personnelles de l'association Neuvième Continent, conforme au RGPD."
      />

      <Header />

      <main className="flex-grow py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-12 policy-content"
          >
            <div className="relative flex flex-col items-center justify-center mb-16 pt-16 md:pt-0">
              <button 
                onClick={() => navigate('/')}
                className="absolute right-0 top-0 md:-top-2 p-3 rounded-full bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-sm hover:shadow-md hover:scale-110 z-10"
                aria-label="Retour à l'accueil"
              >
                <X className="w-6 h-6 md:w-7 md:h-7" />
              </button>
              
              <div className="text-center space-y-4 w-full">
                <h1 className="text-4xl md:text-5xl font-bold font-elegant text-foreground tracking-tight">
                  Politique de Confidentialité
                </h1>
                <p className="text-muted-foreground">Dernière mise à jour : 24 Mars 2026</p>
              </div>
            </div>

            <section>
              <h2>1. Introduction</h2>
              <p>
                L'association <strong>Neuvième Continent</strong> accorde une grande importance à la protection de vos données personnelles. Cette politique de confidentialité vise à vous informer de manière transparente sur la façon dont nous collectons, utilisons, partageons et protégeons vos données dans le cadre de votre utilisation de notre site web et de nos services, conformément au Règlement Général sur la Protection des Données (RGPD) entré en vigueur en 2018.
              </p>
            </section>

            <section>
              <h2>2. Données Collectées et Base Légale</h2>
              <p>Nous collectons différentes catégories de données personnelles selon la nature de nos interactions :</p>
              <ul>
                <li><strong>Données d'identification :</strong> Nom, prénom, pseudonyme (collectées lors de l'inscription ou via les formulaires de contact).</li>
                <li><strong>Données de contact :</strong> Adresse e-mail, numéro de téléphone, adresse postale (pour la gestion des adhésions).</li>
                <li><strong>Données de navigation :</strong> Adresse IP, type de navigateur, pages visitées (via les cookies et outils d'analyse).</li>
              </ul>
              <p>Le traitement de ces données repose sur les bases légales suivantes :</p>
              <ul>
                <li><strong>Le consentement :</strong> Pour l'envoi de newsletters et l'utilisation de cookies non essentiels.</li>
                <li><strong>L'exécution d'un contrat :</strong> Pour la gestion de votre adhésion à l'association.</li>
                <li><strong>L'intérêt légitime :</strong> Pour assurer la sécurité du site et améliorer nos services.</li>
              </ul>
            </section>

            <section>
              <h2>3. Vos Droits (RGPD)</h2>
              <p>Conformément à la réglementation applicable, vous disposez des droits suivants concernant vos données personnelles :</p>
              <ul>
                <li><strong>Droit d'accès (Article 15) :</strong> Vous pouvez obtenir la confirmation que des données vous concernant sont traitées et en demander une copie.</li>
                <li><strong>Droit de rectification (Article 16) :</strong> Vous pouvez demander la correction de données inexactes ou incomplètes.</li>
                <li><strong>Droit à l'effacement / Droit à l'oubli (Article 17) :</strong> Vous pouvez demander la suppression de vos données dans certaines conditions.</li>
                <li><strong>Droit à la limitation du traitement (Article 18) :</strong> Vous pouvez demander de geler temporairement l'utilisation de vos données.</li>
                <li><strong>Droit à la portabilité (Article 20) :</strong> Vous pouvez récupérer les données que vous nous avez fournies dans un format structuré.</li>
                <li><strong>Droit d'opposition (Article 21) :</strong> Vous pouvez vous opposer à tout moment au traitement de vos données pour des raisons tenant à votre situation particulière.</li>
              </ul>
            </section>

            <section>
              <h2>4. Durée de Conservation</h2>
              <p>Nous ne conservons vos données que le temps nécessaire aux finalités pour lesquelles elles ont été collectées :</p>
              <ul>
                <li><strong>Données des membres :</strong> Conservées pendant la durée de l'adhésion, puis archivées pendant 5 ans à des fins probatoires.</li>
                <li><strong>Demandes de contact :</strong> Conservées pendant 1 an après le dernier contact.</li>
                <li><strong>Données de navigation (Cookies) :</strong> Conservées pour une durée maximale de 13 mois.</li>
              </ul>
            </section>

            <section>
              <h2>5. Services Tiers</h2>
              <p>
                Dans le cadre de nos activités, nous pouvons être amenés à utiliser des services tiers qui traitent des données personnelles en notre nom (par exemple, hébergement web, outils d'analyse, gestion de communauté via Discord). Nous nous assurons que ces sous-traitants présentent des garanties suffisantes quant à la mise en œuvre de mesures techniques et organisationnelles appropriées.
              </p>
            </section>

            <section>
              <h2>6. Utilisation des Cookies</h2>
              <p>
                Notre site utilise des cookies pour améliorer votre expérience de navigation. Pour en savoir plus sur les types de cookies que nous utilisons et comment les gérer, veuillez consulter notre <a href="/cookie-policy" className="text-primary hover:underline">Politique de Cookies</a>.
              </p>
            </section>

            <section>
              <h2>7. Mises à Jour de la Politique</h2>
              <p>
                Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment pour nous conformer aux évolutions légales ou réglementaires. En cas de modification substantielle, nous vous en informerons par e-mail ou via un avis bien visible sur notre site.
              </p>
            </section>

            <section className="bg-muted/30 p-8 rounded-2xl border border-border mt-12">
              <h2 className="mt-0">8. Contact et Délégué à la Protection des Données</h2>
              <p>
                Pour toute question relative à cette politique ou pour exercer vos droits, vous pouvez contacter notre responsable du traitement des données :
              </p>
              <ul className="list-none pl-0 mb-0">
                <li><strong>Association :</strong> Neuvième Continent</li>
                <li><strong>Email :</strong> contact@neuvieme-continent.fr</li>
              </ul>
            </section>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default PrivacyPolicyPage;
